import { useState, useEffect } from "react";
import { useForm } from "@inertiajs/react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useBasePath } from "@/lib/paths";
import { CheckCircle2 } from "lucide-react";
import type { NomineeDirectorProtectedPerson } from "@/types";
import { LocationSelector } from "@/components/ui/LocationSelector";
import { DocumentUploader } from "@/components/shared/DocumentUploader";

type LocationItem = { id: string; code: string; name: string };
type StateItem = LocationItem & { country_id: string };
type LocalAreaItem = LocationItem & { state_id: string };

type Props = {
  nomineeDirectorId: string;
  person: NomineeDirectorProtectedPerson | null;
  countries: LocationItem[];
  states: StateItem[];
  localAreas: LocalAreaItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  basePath?: string;
};

export function EditProtectedPersonSheet({
  nomineeDirectorId,
  person,
  countries,
  states,
  localAreas,
  open,
  onOpenChange,
  basePath = "",
}: Props) {
  const defaultBasePath = useBasePath();
  const actualBasePath = basePath || defaultBasePath;
  const [success, setSuccess] = useState(false);

  const form = useForm({
    full_name: person?.full_name || "",
    legal_name: person?.legal_name || "",
    email: person?.email || "",
    phone: person?.phone || "",
    person_kind: person?.person_kind || "individual",
    date_of_birth: person?.date_of_birth || "",
    nationality: person?.nationality || "",
    id_document_number: person?.id_document_number || "",
    country_code: person?.country_code || "",
    state_province: person?.state_province || "",
    city: person?.city || "",
    postal_code: person?.postal_code || "",
    street_address_1: person?.street_address_1 || "",
    street_address_2: person?.street_address_2 || "",
  });

  useEffect(() => {
    if (person && open) {
      form.setData({
        full_name: person.full_name || "",
        legal_name: person.legal_name || "",
        email: person.email || "",
        phone: person.phone || "",
        person_kind: person.person_kind || "individual",
        date_of_birth: person.date_of_birth || "",
        nationality: person.nationality || "",
        id_document_number: person.id_document_number || "",
        country_code: person.country_code || "",
        state_province: person.state_province || "",
        city: person.city || "",
        postal_code: person.postal_code || "",
        street_address_1: person.street_address_1 || "",
        street_address_2: person.street_address_2 || "",
      });
    }
  }, [person?.id, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!person) return;

    form.patch(
      `${actualBasePath}/nominee_directors/${nomineeDirectorId}/protected_persons/${person.id}`,
      {
        onSuccess: () => {
          setSuccess(true);
          setTimeout(() => {
            setSuccess(false);
            onOpenChange(false);
          }, 1000);
        },
      },
    );
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.clearErrors();
      setSuccess(false);
    }
    onOpenChange(open);
  };

  if (success) {
    return (
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="right" className="sm:max-w-lg">
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-sm font-medium text-foreground">
              Person updated
            </p>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{person?.full_name || "Edit Person"}</SheetTitle>
          <SheetDescription>Update protected person details</SheetDescription>
        </SheetHeader>

        <form
          id="person-form"
          onSubmit={handleSubmit}
          className="space-y-5 px-4"
        >
          <div>
            <Label className="text-sm font-medium mb-2 block">
              Person Type
            </Label>
            <RadioGroup
              value={form.data.person_kind}
              onValueChange={(value) => form.setData("person_kind", value)}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="individual" id="edit-individual" />
                <Label
                  htmlFor="edit-individual"
                  className="text-sm font-normal"
                >
                  Individual
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="entity" id="edit-entity" />
                <Label htmlFor="edit-entity" className="text-sm font-normal">
                  Entity
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium">Name</Label>
            <div className="space-y-1.5">
              <Input
                value={form.data.full_name}
                onChange={(e) => form.setData("full_name", e.target.value)}
                placeholder={
                  form.data.person_kind === "entity"
                    ? "Entity name"
                    : "Full name"
                }
                required
              />
              {form.errors.full_name && (
                <p className="text-[12px] text-destructive">
                  {form.errors.full_name}
                </p>
              )}
            </div>
            <Input
              value={form.data.legal_name}
              onChange={(e) => form.setData("legal_name", e.target.value)}
              placeholder="Legal name (optional)"
            />
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium">Contact</Label>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Input
                  type="email"
                  value={form.data.email}
                  onChange={(e) => form.setData("email", e.target.value)}
                  placeholder="Email"
                />
                {form.errors.email && (
                  <p className="text-[12px] text-destructive">
                    {form.errors.email}
                  </p>
                )}
              </div>
              <Input
                type="tel"
                value={form.data.phone}
                onChange={(e) => form.setData("phone", e.target.value)}
                placeholder="Phone"
              />
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium">Date of Birth</Label>
            <Input
              type="date"
              value={form.data.date_of_birth}
              onChange={(e) => form.setData("date_of_birth", e.target.value)}
            />
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium">Nationality</Label>
            <Input
              value={form.data.nationality}
              onChange={(e) => form.setData("nationality", e.target.value)}
              placeholder="e.g. American, British, Nigerian"
            />
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium">ID Document Number</Label>
            <Input
              value={form.data.id_document_number}
              onChange={(e) =>
                form.setData("id_document_number", e.target.value)
              }
              placeholder="Passport or ID number"
            />
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium">Address</Label>
            <Input
              value={form.data.street_address_1}
              onChange={(e) => form.setData("street_address_1", e.target.value)}
              placeholder="Street address"
            />
            <Input
              value={form.data.street_address_2}
              onChange={(e) => form.setData("street_address_2", e.target.value)}
              placeholder="Apt, suite, etc. (optional)"
            />
            <LocationSelector
              countries={countries}
              states={states}
              localAreas={localAreas}
              countryCode={form.data.country_code}
              stateProvince={form.data.state_province}
              city={form.data.city}
              onChange={({ country_code, state_province, city }) => {
                form.setData((d) => ({
                  ...d,
                  country_code,
                  state_province,
                  city,
                }));
              }}
            />
            <Input
              value={form.data.postal_code}
              onChange={(e) => form.setData("postal_code", e.target.value)}
              placeholder="ZIP code"
              className="h-8 text-sm"
            />
          </div>

          <div className="sticky bottom-0 bg-background border-t p-4 -mx-4 mt-4">
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="person-form"
                className="flex-1"
                disabled={form.processing}
              >
                {form.processing ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </form>

        {/* KYC Documents — outside the form to avoid nested form HTML violation */}
        {person && (
          <div className="border-t pt-4 mt-2 px-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
              KYC Documents
            </p>
            <DocumentUploader
              title="KYC Documents"
              documents={person.documents || []}
              uploadPath={`${actualBasePath}/nominee_directors/${nomineeDirectorId}/protected_persons/${person.id}/documents`}
              deletePath={(docId) =>
                `${actualBasePath}/nominee_directors/${nomineeDirectorId}/protected_persons/${person.id}/documents/${docId}`
              }
            />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
